import { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

import { RectangleButtonMulti } from 'us-react-components/dist/RectangleButtonMulti/RectangleButtonMulti';
import { ParentSignupVerticalType, ParentSignupVerticalTypeId, ParentSignupVerticalTypeService, ParentService } from 'us-web-services';
import { NuxContainer } from 'us-react-components/dist/NuxContainer/NuxContainer';
import { useDependencies } from '../../../dependencies';
import { Routes } from '../../../Routes';

export const PetType = observer((): JSX.Element => {
  const {
    stores: { familyRegistration, parent },
    services: { tracker }
  } = useDependencies();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [existingSignupVerticalTypeIds, setExistingSignupVerticalTypeIds] =
    useState<ParentSignupVerticalTypeId[]>(parent.parent.signupVerticalTypes?.map(
      (type: ParentSignupVerticalType) => type.signupVerticalType!.id) || []);

  const [selected, setSelected] = useState<ParentSignupVerticalTypeId[]>(existingSignupVerticalTypeIds);
  const [updating, setUpdating] = useState(false);

  const createUserVerticalType = async (signupVerticalTypeId: ParentSignupVerticalTypeId): Promise<void> => {
    const verticalType = {
      parentId: familyRegistration.profile.id,
      signupVerticalType: {
        id: signupVerticalTypeId,
      }
    };

    try {
      const response = (await ParentSignupVerticalTypeService.create(verticalType)).data;

      const signupVerticalTypes = [
        ...parent.parent?.signupVerticalTypes || [],
        {
          ...verticalType,
          id: response.data.id,
        }
      ];

      setExistingSignupVerticalTypeIds([...existingSignupVerticalTypeIds, signupVerticalTypeId]);

      parent.updateParent({
        ...parent.parent,
        signupVerticalTypes,
      });
    } catch {
      toast.error(t('pages.nux.family.petType.errorSaving'));
      setSelected(selected.filter((selectedId) => selectedId !== signupVerticalTypeId));
    }
  }

  const updateParentProfile = async (): Promise<void> => {
    const profileId = familyRegistration.profile.id || 0;

    const params = {
      id: profileId,
      cat: selected.includes(ParentSignupVerticalTypeId.PET_CARE_CAT),
      dog: selected.includes(ParentSignupVerticalTypeId.PET_CARE_DOG),
      otherPet: selected.includes(ParentSignupVerticalTypeId.PET_CARE_OTHER),
    };

    try {
      const updatedParent = (await ParentService.patch(profileId, params)).data.data;
      parent.updateParent(updatedParent);
    } catch {
      toast.error(t('pages.nux.family.petType.errorSavingPets'));
    }
  }

  const handlePetTypeSelection = async (verticalTypeId: ParentSignupVerticalTypeId): Promise<void> => {
    const trackingProps = {
      elementId: ParentSignupVerticalTypeId[verticalTypeId],
      elementName: 'pet-type-button',
    };

    familyRegistration.setSignupVerticalTypeId(verticalTypeId);

    setUpdating(true);

    await tracker.track('clicked-button', trackingProps);

    if (selected.includes(verticalTypeId)) {
      const newSelected = selected.filter((e) => e !== verticalTypeId)
      setSelected(newSelected);

      if (existingSignupVerticalTypeIds.includes(verticalTypeId)) {
        const existingType = parent.parent?.signupVerticalTypes?.find(
          (type: ParentSignupVerticalType) => type.signupVerticalType?.id === verticalTypeId);

        if (existingType?.id && familyRegistration.profile.id) {
          const deleteConfig = {
            params: {
              parentId: familyRegistration.profile.id,
            },
          };

          try {
            await ParentSignupVerticalTypeService.delete(existingType.id, deleteConfig);
            const signupVerticalTypes = parent.parent.signupVerticalTypes?.filter(
              (type: ParentSignupVerticalType) => type.signupVerticalType?.id !== verticalTypeId);

            parent.updateParent({
              ...parent.parent,
              signupVerticalTypes,
            });

            setExistingSignupVerticalTypeIds(existingSignupVerticalTypeIds.filter(id => id !== verticalTypeId));
          } catch {
            toast.error('Error removing pet type.');
            setSelected(selected);
          }
        }
      }
    } else {
      setSelected([...selected, verticalTypeId]);
      await createUserVerticalType(verticalTypeId);
    }

    setUpdating(false);
  }

  familyRegistration.setCurrentRoute(Routes.parent.registration.petType);

  const handleContinue = async (): Promise<void> => {
    setUpdating(true);
    familyRegistration.setDog(selected.includes(ParentSignupVerticalTypeId.PET_CARE_DOG));
    familyRegistration.setCat(selected.includes(ParentSignupVerticalTypeId.PET_CARE_CAT));
    familyRegistration.setOtherPet(selected.includes(ParentSignupVerticalTypeId.PET_CARE_OTHER));
    await updateParentProfile();
    setUpdating(false);
    navigate(Routes.parent.registration.frequency);
  }

  return (
    <NuxContainer
      familyRegistration={familyRegistration}
      navigate={navigate}
      title={t('pages.nux.family.petType.title')}
      tracker={tracker}
      onContinue={handleContinue}
      isContinueDisabled={updating || !selected.length}>
      <div className='flex flex-col py-8 w-[90%] md:w-[33%]'>
        <div className='pb-4'>
          <RectangleButtonMulti
            title={t('pages.nux.family.petType.rectangleButtons.dogs')}
            disabled={updating}
            onClick={async (): Promise<void> => handlePetTypeSelection(ParentSignupVerticalTypeId.PET_CARE_DOG)}
            selected={selected.includes(ParentSignupVerticalTypeId.PET_CARE_DOG)}
          />
        </div>
        <div className='pb-4'>
          <RectangleButtonMulti
            title={t('pages.nux.family.petType.rectangleButtons.cats')}
            disabled={updating}
            onClick={async (): Promise<void> => handlePetTypeSelection(ParentSignupVerticalTypeId.PET_CARE_CAT)}
            selected={selected.includes(ParentSignupVerticalTypeId.PET_CARE_CAT)}
          />
        </div>
        <RectangleButtonMulti
          title={t('pages.nux.family.petType.rectangleButtons.others')}
          disabled={updating}
          onClick={async (): Promise<void> => handlePetTypeSelection(ParentSignupVerticalTypeId.PET_CARE_OTHER)}
          selected={selected.includes(ParentSignupVerticalTypeId.PET_CARE_OTHER)}
        />
      </div>
    </NuxContainer>
  )
});
